/**
 * **Has this actor got the action left?** — the per-grain usage ledgers (turn/round/scene/
 * limited) and how many reactions remain this round. The generic use-legality gate
 * (`checkActionUseLegality`) lives beside the derivation it reads, in `./actionId`: it resolves a
 * granted id through `resolveAffordance`, whose module imports this one.
 */

import type { ActivationState, EncounterState } from "@lancer-vtt/domain";
import { ACTIONS_PER_TURN, CORE_POWER_POOL, REACTION_SLOT_ALLOWANCE, canUseAction, findAffordance, grantedActionAllowanceBonus, reactionSlotOpen } from "@lancer-vtt/domain";
import type { EncounterIntent } from "@lancer-vtt/shared";
import { ok, err } from "@lancer-vtt/shared";
import type { BudgetVerdict } from "./types";
import { affordable } from "../events/gameEvents";

/**
 * May `actor` still use the once-per-turn RAW action `id` this turn, allowance applied? The
 * ledger read behind {@link checkActionUseLegality}, exported for a caller that already knows the
 * id — `stop_barrage_as_skirmish` asks it of `"skirmish"` before re-pricing a Barrage. `true` on
 * an idle turn (enforcement only governs a running fight).
 */
export type CanStillUseActionArgs = {
  state: EncounterState;
  actor: string;
  id: string;
  /**
   * The ledger slot to COUNT, when it differs from the slot `id` names the allowance for. Only
   * Quick Tech sets it: the allowance is `"quick_tech"`'s, counted per option
   * ({@link import("./actionId").quickTechOptionLedgerId}). Omitted, `id` is both.
   */
  countKey?: string;
};

export function canStillUseAction(args: CanStillUseActionArgs): boolean {
  const { state, actor, id, countKey = id } = args;
  const turn = state.activation.turn;
  if (turn.phase !== "acting") return true;
  // A granted `1/turn` action's allowance comes from its frozen affordance record's `cap.max`
  // (affordance-key keyed), not `actionAllowances` (RAW-catalog-id keyed) — the two never
  // collide (different id namespaces), so trying `actionAllowances` first, then the record's cap,
  // then the RAW default of 1 is safe either way. The lookup is an EXACT match on the frozen
  // table, bit-for-bit what the deleted `base.actionUsageCaps?.[id]` raw read produced.
  //
  // The `cap.max` fallback is ONLY valid when that cap's own grain IS `"turn"` —
  // its `max` is a per-turn allowance in that case (e.g. Boost-shaped `1/turn`, or `2/turn`).
  // Reading a scene cap's `max` (e.g. a `Limited 3` charge item's `{grain:"scene", max:3}`) as a
  // TURN allowance would wrongly let the SAME granted action fire up to `max` times in one turn
  // instead of RAW's default once. Gate the fallback on `grain === "turn"`; any other grain (or
  // no cap at all) falls through to the RAW default of `1`.
  const cap = findAffordance(state.combatants[actor]?.base.affordances, id)?.cap;
  const staticAllowance =
    state.combatants[actor]?.base.actionAllowances?.[id] ??
    (cap?.grain === "turn" ? cap.max : undefined) ??
    1;
  // A `grant_action_allowance` DSL atom fired this turn (t_executioner:1's on-hit "additional
  // Skirmish/Barrage") ADDS a runtime bonus on top of the static allowance — never replaces it,
  // so a spawn-frozen override and a mid-combat grant compose additively.
  const allowance = staticAllowance + grantedActionAllowanceBonus(state.combatants[actor]?.current.resources, id);
  return affordable({ kind: "action_points", carrier: actor, points: 0, actionId: countKey, allowance }, state, undefined);
}

/**
 * The four reset grains {@link checkActionAvailable} composes over — turn, round, scene, or
 * weapon `Limited`. `"limited"`'s `actionId` is a **weapon instance key**
 * ({@link ../../../domain/src/encounter/types weaponInstanceKey}), not a free action id.
 * Since `affordance-table` Phase 3.1 the two share ONE substrate — `current.affordanceState`,
 * a flat table whose keys are affordance keys and weapon instance keys alike (`decisions/0176`
 * ruling 1) — with the same consumed-count polarity. The two key schemes are disjoint, so the
 * grains stay distinct at the CALLER (which fact is being counted) rather than in the storage.
 */
export type ActionGrain = "turn" | "round" | "scene" | "limited";

/** Named arguments for {@link checkActionAvailable}. */
export type CheckActionAvailableArgs = {
  state: EncounterState;
  /** The combatant whose usage ledger to check. */
  actor: string;
  /**
   * The ledger key this use would be recorded under — a free action-id string for
   * `"turn"`/`"round"`/`"scene"`, or a **weapon instance key**
   * ({@link ../../../domain/src/encounter/types weaponInstanceKey}) for `"limited"`.
   */
  actionId: string;
  /** Which per-cadence substrate to read. */
  grain: ActionGrain;
  /** The cadence's cap (default 1 — RAW's usual "once"). */
  max?: number;
}

/**
 * **The single shared usability predicate for the action-usage grains** — composes internally so
 * every consumer (a gate, the CLI, a chooser-greying read) asks ONE question rather
 * than picking a substrate by hand:
 *   - **`grain: "turn"`** delegates to {@link canUseAction} (`@lancer-vtt/domain`) — the
 *     SAME once-per-turn wall `checkActionUseLegality` reads for core verbs, and the
 *     one a granted `1/turn` action reuses.
 *   - **`grain: "round"`** reads {@link ../../../domain/src/encounter/types
 *     ActivationState.actionRoundUses} via `canUseRoundAction`.
 *   - **`grain: "scene"`** reads the actor's `current.affordanceState` via `canUseSceneAction`.
 *   - **`grain: "limited"`** reads the actor's `current.affordanceState` via `canUseLimitedWeapon`
 *     — the SAME ledger and the same consumed-count shape as `"scene"`, keyed by a weapon
 *     instance key rather than an affordance key (weapon `Limited` rides `WeaponProfile`, never
 *     the granted-action descriptor). {@link checkLimited} is the
 *     pre-dice-ladder wrapper a weapon-fire gate calls, translating a `weapon`/`profile`
 *     selection into this grain.
 *
 * **HARD RULE: this is the only door.** No other read site (a gate, the pre-dice ladder, a
 * chooser) may consult `actionRoundUses`/`current.affordanceState`/`actionUses` directly for an
 * action-usage-limits grain — route it through here instead, or the substrates decay into forked
 * ledgers. (Recharge's `current.affordanceState[key].discharged` and Loading's `loaded:` are a SEPARATE
 * usability-predicate pair — {@link checkWeaponRecharge}/{@link checkLoaded} — this predicate
 * does not fold those in.)
 *
 * Polarity is uniform across every grain: consumed-count, gate `consumed < max`, absent =
 * 0 consumed = full. Returns a verdict a room/CLI/gate surfaces directly (a rejection is a
 * logged no-op before any dice/effect).
 */
export function checkActionAvailable(args: CheckActionAvailableArgs): BudgetVerdict {
  const { state, actor, actionId, grain, max = 1 } = args;
  const name = state.combatants[actor]?.name ?? actor;
  switch (grain) {
    case "turn": {
      const turn = state.activation.turn;
      if (turn.phase !== "acting" || turn.actor !== actor) return ok(undefined);
      // A `grant_action_allowance` bonus composes with a granted action's OWN frozen `1/turn`
      // cap too (the same additive rule `checkActionUseLegality` applies to core verbs) — a
      // triggered grant naming a composite granted-action id works.
      const bonus = grantedActionAllowanceBonus(state.combatants[actor]?.current.resources, actionId);
      if (affordable({ kind: "action_points", carrier: actor, points: 0, actionId, allowance: max + bonus }, state, undefined)) return ok(undefined);
      return err(`${actionId}: ${name} has already used this action this turn (once per turn)`);
    }
    case "round": {
      if (affordable({ kind: "action_use", carrier: actor, key: actionId, grain: "round", max }, state, undefined)) return ok(undefined);
      return err(`${actionId}: ${name} has already used this action this round`);
    }
    case "scene": {
      if (affordable({ kind: "action_use", carrier: actor, key: actionId, grain: "scene", max }, state, undefined)) return ok(undefined);
      if (actionId === CORE_POWER_POOL) return err(`${name} has no Core Power left: it returns on a Full Repair`);
      return err(`${actionId}: ${name} has already spent this action for the encounter`);
    }
    case "limited": {
      if (affordable({ kind: "weapon", carrier: actor, key: actionId, spend: "limited", limit: max }, state, undefined)) return ok(undefined);
      return err(`${actionId}: ${name} is out of charges (Limited ${max})`);
    }
  }
}

/** The full per-turn action-point allotment (re-exported for the room/CLI). */
export { ACTIONS_PER_TURN, REACTION_SLOT_ALLOWANCE };

/**
 * The GLOBAL reaction slot's remaining count for `actor` — **0 or 1**: refreshed at `actor`'s own
 * turn start, not the round boundary (see {@link reactionSlotOpen}, which this wraps as a number
 * for display). This does NOT fold in the per-named-reaction cap
 * ({@link canUseReaction}) — a caller that needs to
 * know whether a SPECIFIC named reaction (Overwatch/Brace) is still usable must
 * check that separately (the frontend's generic REACT pip only needs the slot fact; the room's
 * `checkBudget` checks both).
 */
export function reactionsLeft(activation: ActivationState, actor: string): number {
  return reactionSlotOpen(activation, actor) ? 1 : 0;
}

